/* eslint-disable react-hooks/exhaustive-deps */
// Could not fix all "react-hooks/exhaustive-deps" warnings, and make component work as it should. Needs more researching, maybe.
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  ProductListing,
  SortingOption,
  BrandFilterOption,
  ProductListingTranslations,
  CustomPaginator,
  PageChangeInfo,
  CustomPaginatorProps,
  SelectedBrandFilters,
} from '@cdaas/reusable-components-daas';
import { Gutter, Headline } from '@gaiads/telia-react-component-library';
import { debounce } from 'lodash';
import {
  PagedItemsOverviewEnvelope,
  SalesChannel,
  BundleOverview,
  SingleVariantProductOverview,
  MultiVariantProductOverview,
  PageMetadata,
} from '@cdaas/api-clients/dist/product-service-openapi';
import { OrganizationResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';

import { productServiceSalesChannelItemsClient } from '../../../api-client/product-service-client';
import { selectOrganization, selectSalesChannel, selectSelectedUser } from '../../../store/order';
import UserOrOrganizationDetail from './UserOrOrganizationDetail';
import { removeStorageItems, UserResponseWithUserGroup } from '../../../utils';

export type WrapperItemOverview = BundleOverview | SingleVariantProductOverview | MultiVariantProductOverview;

type PageInfo = NonNullable<CustomPaginatorProps<WrapperItemOverview>['activatePageInfoObject']>;

type FilterProductsParams = {
  pageInfo: PageInfo;
  brands: string;
  name: string;
  sorting?: SortingOption;
};

const MOBILE_BREAKPOINT = 1080;

const NUMBER_OF_PRODUCTS_PER_PAGE = 12;

export default function SelectProduct() {
  const salesChannel: SalesChannel | null = useSelector(selectSalesChannel);
  const organization: OrganizationResponse | null = useSelector(selectOrganization);
  const user: UserResponseWithUserGroup | null = useSelector(selectSelectedUser);
  const [products, setProducts] = useState<WrapperItemOverview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const history = useHistory();
  const { t, i18n } = useTranslation('orderFlow');
  const numberOfProductColumns = 3;

  const [paging, setPaging] = useState<PageInfo>({
    size: NUMBER_OF_PRODUCTS_PER_PAGE,
    current_page: 0, // custom paginator indexing should start from 0,
    total_pages: 0,
    total_items: 0,
  });
  const [pageMetadata, setPageMetadata] = useState<PageMetadata>();

  const [brandOptions, setBrandOptions] = useState<BrandFilterOption[]>([]);
  const [selectedBrandFilters, setSelectedBrandFilters] = useState<SelectedBrandFilters>({});

  const [sortingOptions, setSortingOptions] = useState<SortingOption[]>([
    { label: t('selectProduct.productListing.sortingPriceAsc'), value: 'price_asc', selected: false },
    { label: t('selectProduct.productListing.sortingPriceDesc'), value: 'price_desc', selected: false },
  ]);

  const [textSearch, setTextSearch] = useState('');

  const checkIsMobile = () => {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  };

  const [isMobile, setIsMobile] = useState(checkIsMobile());

  useEffect(() => {
    function handleResize() {
      setIsMobile(checkIsMobile());
    }

    removeStorageItems(['tItem'], 'session'); // clear selected bundle from session storage

    window.addEventListener('resize', handleResize);
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timeOutHandle = setTimeout(() => {
      if (!salesChannel || !organization) {
        history.push('/');
      }
    }, 500);
    return () => clearTimeout(timeOutHandle);
    // want to run this only once
  }, []);

  useEffect(() => {
    const fetchBrands = async () => {
      if (salesChannel?.uuid) {
        try {
          const brands = (await productServiceSalesChannelItemsClient.getSalesChannelBrands(salesChannel?.uuid)).data.brands;
          const brandOptions = brands.map(brand => ({ label: brand }));
          setBrandOptions(brandOptions);
        } catch {
          // Just show no brand options if fetching of the brands fail - obviously shouldn't normally happen
        }
      }
    };
    fetchBrands();
  }, []);

  const getProductsAndBundles = async ({ brands, sorting, name, pageInfo }: FilterProductsParams): Promise<void> => {
    try {
      const data: PagedItemsOverviewEnvelope = (
        await productServiceSalesChannelItemsClient.getSalesChannelItems(
          salesChannel?.uuid || '',
          undefined,
          pageInfo.current_page,
          pageInfo.size,
          brands,
          name,
          undefined,
          undefined,
          user?.policies.effective_lease_procurement_limit || undefined,
          sorting?.value.split('_')[0] as 'name' | 'brand' | 'price' | 'created' | 'updated' | undefined,
          sorting?.value.split('_')[1] as 'asc' | 'desc' | undefined,
          true,
          organization?.locale.language,
        )
      ).data;
      setProducts(data.items);
      setPaging({
        ...pageInfo,
        total_items: data.page_metadata.total_items,
        total_pages: data.page_metadata.total_pages,
      });
      setPageMetadata(data.page_metadata);
    } catch (error) {
      console.error(error);
      setError(t('selectProduct.productListing.getProductsErrorTitle'));
    } finally {
      setLoading(false);
    }
  };

  const productListingTranslations: ProductListingTranslations = {
    textSearchTitle: t('selectProduct.productListing.textSearchTitle'),
    textSearchPlaceholder: t('selectProduct.productListing.textSearchPlaceholder'),
    productsSortLabel: t('selectProduct.productListing.productsSortLabel'),
    brandFilterTitle: t('selectProduct.productListing.brandFilterTitle'),
    mobileSearchAccordionTitle: '',
    productPriceLabel: t('selectProduct.productListing.productPriceLabel'),
    noProductsFoundTitle: t('selectProduct.productListing.noProductsFoundTitle'),
    currency: organization?.locale.currency.toUpperCase() || '',
    month: t('selectProduct.productListing.month'),
    monthPeriod: t('selectProduct.productListing.monthPeriod'),
    singlePayment: t('selectProduct.productListing.singlePayment'),
    vat: t('selectProduct.productListing.vat'),
    priceIncludingVat: t('selectProduct.productListing.priceIncludingVat'),
    priceExcludingVat: t('selectProduct.productListing.priceExcludingVat'),
    filterResultsButton: t('selectProduct.productListing.filterResultsButton'),
    showButton: t('selectProduct.productListing.showButton'),
  };

  const handleItemClick = (item: WrapperItemOverview): void => {
    switch (item.type) {
      case 'BundleOverview':
        // TODO - save bundle to Redux
        history.push(`bundle/${item.id}`);
        break;
      case 'SingleVariantProductOverview':
        // FUTURE - MVP only Bundles
        break;
      case 'MultiVariantProductOverview':
        // FUTURE - MVP only Bundles
        break;
    }
  };

  const debouncedFilterProducts = useCallback(debounce(getProductsAndBundles, 500), []);

  useEffect(() => {
    setLoading(true);
    setError('');
    debouncedFilterProducts({
      brands: Object.keys(selectedBrandFilters).join(','),
      sorting: sortingOptions.find(s => s.selected),
      name: textSearch,
      pageInfo: paging,
    });
  }, [i18n.language, paging.current_page, selectedBrandFilters, sortingOptions, textSearch]);

  useEffect(() => {
    setPaging({ ...paging, current_page: 0 });
  }, [selectedBrandFilters, textSearch]);

  const onPagingChange = (pageChangeInfo: PageChangeInfo<WrapperItemOverview>): void => {
    setPaging({ ...paging, current_page: pageChangeInfo.nextChosenPageNumber });
  };

  return (
    <>
      <Gutter size={'layout-5'} />
      <Headline size={'lg'}>{t('selectProduct.title')}</Headline>
      <Gutter size={'layout-9'} />
      <UserOrOrganizationDetail user={user} organization={organization} />
      <Gutter size={'layout-7'} />
      <ProductListing
        products={products}
        loading={loading}
        numberOfProductColumns={numberOfProductColumns}
        onItemClick={(item: WrapperItemOverview) => handleItemClick(item)}
        errorText={error}
        translations={productListingTranslations}
        brandFilters={brandOptions}
        selectedBrandFilters={selectedBrandFilters}
        onSelectedBrandFiltersChange={setSelectedBrandFilters}
        sortingOptions={sortingOptions}
        onSortingOptionsChange={setSortingOptions}
        textSearch={textSearch}
        onTextSearchChange={setTextSearch}
        mobileLayout={isMobile}
        locale={organization?.locale.language}
      />
      {!loading && !error && (
        <CustomPaginator
          activatePageInfoObject={pageMetadata}
          onPageChange={onPagingChange}
          paginatorStyleProps={{
            pageSelectorProps: { centered: true },
          }}
        />
      )}
    </>
  );
}
